import React, { useState, useEffect } from "react";
import {
  Settings,
  ShieldCheck,
  Headphones,
  Phone,
  Clock,
  IndianRupee,
  Save,
  CheckCircle2,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import { toast } from "sonner";

export default function MasterAdminSettings() {
  const { masterApi } = useMasterAdmin();
  const [graceDays, setGraceDays] = useState(7);
  const [supportName, setSupportName] = useState("Kunal Warkante");
  const [supportPhone, setSupportPhone] = useState("9545262332");
  const [supportEmail, setSupportEmail] = useState("jsmdigitalsolution@gmail.com");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const res = await masterApi.get("/master-admin/settings");
        if (res.data) {
          setGraceDays(res.data.grace_period_days || 7);
          setSupportName(res.data.support_name || "Kunal Warkante");
          setSupportPhone(res.data.support_phone || "9545262332");
          setSupportEmail(res.data.support_email || "jsmdigitalsolution@gmail.com");
        }
      } catch (err) {
        console.warn("Failed to load settings", err);
      } finally {
        setLoading(false);
      }
    }
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await masterApi.put("/master-admin/settings", {
        grace_period_days: parseInt(graceDays, 10) || 7,
        support_name: supportName.trim(),
        support_phone: supportPhone.trim(),
        support_email: supportEmail.trim(),
      });
      toast.success("Master settings saved and broadcasted successfully");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-4xl mx-auto pb-10">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
          <span>System & Support Settings</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold">
            Configuration
          </span>
        </h1>
        <p className="text-xs text-zinc-400 mt-0.5">
          Configure customer support executive details, membership grace period tolerances, and global system metadata.
        </p>
      </div>

      <form onSubmit={handleSave} className="flex flex-col gap-6">
        {/* Support Contact Settings Card */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 shadow-xl flex flex-col gap-4">
          <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
            <Headphones className="w-5 h-5 text-amber-400" />
            <h2 className="text-sm font-bold text-white">Customer Support Configuration</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="flex flex-col gap-1.5">
              <label className="font-semibold text-zinc-300">Support Executive Name</label>
              <input
                type="text"
                required
                value={supportName}
                onChange={(e) => setSupportName(e.target.value)}
                placeholder="Kunal Warkante"
                className="px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-semibold text-zinc-300">Direct Support Phone Number</label>
              <input
                type="text"
                required
                value={supportPhone}
                onChange={(e) => setSupportPhone(e.target.value)}
                placeholder="9545262332"
                className="px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono"
              />
            </div>

            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <label className="font-semibold text-zinc-300">Official Support Email</label>
              <input
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                placeholder="jsmdigitalsolution@gmail.com"
                className="px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
              />
            </div>
          </div>
        </div>

        {/* License Grace Period Settings Card */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 shadow-xl flex flex-col gap-4">
          <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
            <Clock className="w-5 h-5 text-amber-400" />
            <h2 className="text-sm font-bold text-white">Membership Grace Period</h2>
          </div>

          <div className="flex flex-col gap-3 text-xs">
            <div className="flex flex-col gap-1.5 max-w-xs">
              <label className="font-semibold text-zinc-300">Grace Period (Days)</label>
              <input
                type="number"
                min="0"
                max="30"
                required
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                className="px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono text-sm"
              />
            </div>
            <p className="text-zinc-400 leading-relaxed">
              When a user's 30-day membership expires, PrintQ displays a non-blocking warning banner for this duration instead of immediately locking the software. After the grace period passes, full software access is locked until renewed.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 active:scale-95 text-zinc-950 font-bold text-xs shadow-lg shadow-amber-500/20 transition-all cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save System Settings</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
